import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedService } from '../shared.service';
import { CommonService } from '../services/common.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ConfigService } from '../services/config.service';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
})
export class RegistrationComponent {
  form: FormGroup = new FormGroup({
    schoolName: new FormControl('', [Validators.required]),
    adminFirstName: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z]+$/),
    ]),
    adminLastName: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z]+$/),
    ]),
    email: new FormControl('', [Validators.required, Validators.email]),
    contactNumber: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[0-9]{10,15}$/),
    ]),
    address: new FormControl('', [Validators.required]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(7),
      Validators.pattern(/^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[A-Z]).+$/),
    ]),
    confirmPassword: new FormControl('', [Validators.required]),
    // License Key field
    licenseKey: new FormControl('', [
      Validators.required,
      Validators.pattern(/^\d{16}$/),
    ]), // 16 digit validation
  });

  username: string = '';
  password: string = '';
  isLoading: boolean = false;
  showPasswordVal: boolean = false;
  showConfirmPasswordVal: boolean = false;

  constructor(
    private authService: SharedService,
    private _common: CommonService,
    private http: HttpClient,
    private router: Router,
    private _config: ConfigService
  ) {}

  register() {
    if (this.form.valid) {
      if (!this.passwordsMatch()) {
        this._common.openSnackBarError('Passwords do not match');
        return;
      }

      this.isLoading = true;

      // license key field
      const licenseKey = this.form.get('licenseKey')?.value;

      // school registration form
      const registrationData = {
        firstName: this.form.get('adminFirstName')?.value,
        middleName: '',
        lastName: this.form.get('adminLastName')?.value,
        email: this.form.get('email')?.value,
        password: this.form.get('password')?.value,
        confirmPassword: this.form.get('confirmPassword')?.value,
        mobile: this.form.get('contactNumber')?.value,
        schoolName: this.form.get('schoolName')?.value,
        schoolAddress: this.form.get('address')?.value,
      };

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
      });

      //validate license key before registration
      this.http
        .post(
          `${this._config._gdsBaseUrl}/license-keys/activate/`,
          { license_key: licenseKey },
          { headers }
        )
        .pipe(
          catchError((error) => {
            this.isLoading = false;
            let errorMessage = 'License validation failed. Please try again.';

            if (error.error && error.error.message) {
              errorMessage = error.error.message;
            } else if (error.status === 400) {
              errorMessage = 'Invalid License Key. Please check and try again.';
            } else if (error.status === 404) {
              errorMessage =
                'License Key not found. Please provide a valid license key.';
            }

            this._common.openSnackBarError(errorMessage);
            return of(null); // Return null to stop chain
          }),
          // switchMap to chain registration ONLY if license is valid
          switchMap((licenseResponse: any) => {
            // Stop if license validation returned null (error case)
            if (!licenseResponse) {
              return of(null);
            }

            // checking the licenseKey already activated
            if(
              licenseResponse.success && 
              licenseResponse.license_key?.is_active === true
            ){
              this.isLoading = false;
              this._common.openSnackBarError(
                "License Key is already activated. Please use a different license key"
              );

              return of(null)
            }

            // if License is valid, proceed with school registration
            const requestBody = {
              ...registrationData,
              licenseKey: licenseKey,
            };

            return this.http.post(
              `${this._config._url}AdminLogin/register-admin`,
              requestBody,
              { headers }
            ).pipe(
              catchError((error)=>{
                this.isLoading = false;
                let errorMessage = 'School registraton failed. License Key not activated';

                if(error.error && error.error.message){
                  errorMessage = error.error.message;
                }

                this._common.openSnackBarError(errorMessage);
                return of(null);

              })
            )
          }),

          switchMap((schoolResponse: any)=>{
            if(!schoolResponse){
              return of(null);
            }

            if(!schoolResponse.success){
              this.isLoading = false;
              this._common.openSnackBarError(
                schoolResponse.message || "School registration failed"
              );
              return of(null);
            }

            return this.http.post(
              `${this._config._gdsBaseUrl}/license-keys/activate/`,
              {
                license_key: licenseKey,
                is_active: true // Activate the license
              },
              {headers}
            ).pipe(
              catchError((error)=>{
                this.isLoading = false;
                this._common.openSnackBarError(
                  'School created but license activation failed, Please contact support'
                );
                return of(null);
              }),

              switchMap((activationResponse: any)=>{
                return of({
                  schoolResponse,
                  activationResponse
                });
              })
            );
          })
        )
        .subscribe({
          next: (response: any) => {
            // skip if response is null (from error cases)
            if (!response) {
              return;
            }

            this.isLoading = false;

            if (response.success) {
              this._common.openSnackBar(
                'School registered and activated successfully! Redirecting to login...'
              );
              this.form.reset();

              setTimeout(() => {
                this.router.navigate(['/login']);
              }, 2000);
            } else {
              this._common.openSnackBarError(
                response.message || 'Registration failed'
              );
            }
          },
          error: (error) => {
            this.isLoading = false;
            let errorMessage = 'Registration failed. Please try again.';
            if (error.error && error.error.message) {
              errorMessage = error.error.message;
            } else if (error.statusText) {
              errorMessage = error.statusText;
            }
            this._common.openSnackBarError(errorMessage);
          },
        });
    } else {
      this.markFormGroupTouched();
      this.validateAllFormFields();
    }
  }

  validateAllFormFields() {
    const controls = this.form.controls;

    for (const field in controls) {
      if (controls[field].invalid) {
        let errorMessage = '';

        if (field === 'email' && controls[field].errors?.['email']) {
          errorMessage =
            'Please provide a valid email format (e.g., email@example.com)';
        } else if (
          field === 'contactNumber' &&
          controls[field].errors?.['pattern']
        ) {
          errorMessage = 'Mobile number must be at least 10 digits long';
        } else if (field === 'password') {
          if (controls[field].errors?.['minlength']) {
            errorMessage = 'Password must be at least 7 characters long';
          } else if (controls[field].errors?.['pattern']) {
            errorMessage =
              'Password must contain at least one uppercase letter, one special character, and one digit';
          } else if (controls[field].errors?.['required']) {
            errorMessage = 'Password is required';
          }
        } else if (
          (field === 'adminFirstName' || field === 'adminLastName') &&
          controls[field].errors?.['pattern']
        ) {
          errorMessage = `${this.getFieldDisplayName(
            field
          )} must contain only letters (no spaces, numbers, or special characters)`;
        } else if (controls[field].errors?.['required']) {
          errorMessage = `${this.getFieldDisplayName(field)} is required`;
        }

        if (errorMessage) {
          this._common.openSnackBarError(errorMessage);
          return;
        }
      }
    }
  }

  login() {
    if (!this.authService.login(this.username, this.password)) {
      alert('Invalid credentials');
    }
  }

  roleOption(role: string) {
    if (role === 'admin') {
      this.username = 'admin@gmail.com';
      this.password = 'admin';
    } else if (role === 'teacher') {
      this.username = 'teacher@gmail.com';
      this.password = 'teacher';
    } else if (role === 'parent') {
      this.username = 'parent@gmail.com';
      this.password = 'parent';
    } else if (role === 'transport') {
      this.username = 'transport@gmail.com';
      this.password = 'transport';
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        return `${this.getFieldDisplayName(fieldName)} is required`;
      }
      if (fieldName === 'email' && field.errors['email']) {
        return 'Please provide a valid email format (e.g., email@example.com)';
      }
      if (fieldName === 'contactNumber' && field.errors['pattern']) {
        return 'Mobile number must be at least 10 digits long';
      }
      if (fieldName === 'licenseKey' && field.errors['pattern']) {
        return 'License key must be exactly 16 digits';
      }
      if (fieldName === 'password') {
        if (field.errors['minlength']) {
          return 'Password must be at least 7 characters long';
        }
        if (field.errors['pattern']) {
          return 'Password must contain at least one uppercase letter, one special character, and one digit';
        }
      }
      if (
        (fieldName === 'adminFirstName' || fieldName === 'adminLastName') &&
        field.errors['pattern']
      ) {
        return `${this.getFieldDisplayName(
          fieldName
        )} must contain only letters (no spaces, numbers, or special characters)`;
      }
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const fieldNames: { [key: string]: string } = {
      schoolName: 'School Name',
      adminFirstName: 'First Name',
      adminLastName: 'Last Name',
      email: 'Email',
      contactNumber: 'Mobile Number',
      address: 'School Address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      licenseKey: 'License Key',
    };
    return fieldNames[fieldName] || fieldName;
  }

  showPassword(type: any) {
    if (type == 'p') {
      this.showPasswordVal = !this.showPasswordVal;
    } else {
      this.showConfirmPasswordVal = !this.showConfirmPasswordVal;
    }
  }

  passwordsMatch(): boolean {
    const password = this.form.get('password')?.value;
    const confirmPassword = this.form.get('confirmPassword')?.value;
    return password === confirmPassword;
  }

  //license key validation
  formatLicenseKey(event: any) {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits

    if (value.length > 16) {
      value = value.substring(0, 16);
    }

    this.form.get('licenseKey')?.setValue(value, { emitEvent: false });
  }
}
